import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Plus, Edit, Trash2, Eye, CheckCircle, Clock, XCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import CaseStudyForm from './CaseStudyForm';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [contacts, setContacts] = useState([]);
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCaseStudyForm, setShowCaseStudyForm] = useState(false);
  const [editingCaseStudy, setEditingCaseStudy] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      const [contactsRes, caseStudiesRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/contacts`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/admin/case-studies`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (!contactsRes.ok || !caseStudiesRes.ok) {
        throw new Error('Failed to fetch data');
      }

      const contactsData = await contactsRes.json();
      const caseStudiesData = await caseStudiesRes.json();

      setContacts(contactsData);
      setCaseStudies(caseStudiesData);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load data. Please login again.",
        variant: "destructive"
      });
      localStorage.removeItem('admin_token');
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin');
  };

  const updateContactStatus = async (contactId, status) => {
    try {
      const token = localStorage.getItem('admin_token');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${BACKEND_URL}/api/admin/contacts/${contactId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });

      if (!response.ok) throw new Error('Failed to update');

      toast({
        title: "Success",
        description: "Contact status updated"
      });
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive"
      });
    }
  };

  const deleteCaseStudy = async (caseStudyId) => {
    if (!window.confirm('Are you sure you want to delete this case study?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${BACKEND_URL}/api/admin/case-studies/${caseStudyId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to delete');

      toast({
        title: "Success",
        description: "Case study deleted"
      });
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete case study",
        variant: "destructive"
      });
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      new: { icon: Clock, color: 'bg-blue-100 text-blue-700', label: 'New' },
      contacted: { icon: CheckCircle, color: 'bg-yellow-100 text-yellow-700', label: 'Contacted' },
      closed: { icon: XCircle, color: 'bg-green-100 text-green-700', label: 'Closed' }
    };
    const badge = badges[status] || badges.new;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${badge.color}`}>
        <Icon size={12} className="mr-1" />
        {badge.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          <p className="mt-4 text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-slate-900">
              Mid<span className="text-blue-500">Skale</span> Admin
            </h1>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="flex items-center"
            >
              <LogOut size={16} className="mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="contacts" className="w-full">
          <TabsList className="mb-8">
            <TabsTrigger value="contacts">
              Contact Submissions ({contacts.length})
            </TabsTrigger>
            <TabsTrigger value="case-studies">
              Case Studies ({caseStudies.length})
            </TabsTrigger>
          </TabsList>

          {/* Contacts Tab */}
          <TabsContent value="contacts">
            <div className="bg-white rounded-lg shadow">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Service</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-200">
                    {contacts.map((contact) => (
                      <tr key={contact.id} className="hover:bg-slate-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-slate-900">{contact.name}</div>
                            <div className="text-sm text-slate-500">{contact.company || 'No company'}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">{contact.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">{contact.service}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(contact.status)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <select
                            value={contact.status}
                            onChange={(e) => updateContactStatus(contact.id, e.target.value)}
                            className="border border-slate-300 rounded px-2 py-1 text-sm"
                          >
                            <option value="new">New</option>
                            <option value="contacted">Contacted</option>
                            <option value="closed">Closed</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Case Studies Tab */}
          <TabsContent value="case-studies">
            <div className="mb-6">
              <Button
                onClick={() => {
                  setEditingCaseStudy(null);
                  setShowCaseStudyForm(true);
                }}
                className="bg-blue-500 hover:bg-blue-600 text-white"
              >
                <Plus size={16} className="mr-2" />
                Add New Case Study
              </Button>
            </div>

            {showCaseStudyForm && (
              <CaseStudyForm
                caseStudy={editingCaseStudy}
                onClose={() => {
                  setShowCaseStudyForm(false);
                  setEditingCaseStudy(null);
                }}
                onSave={() => {
                  setShowCaseStudyForm(false);
                  setEditingCaseStudy(null);
                  fetchData();
                }}
              />
            )}

            <div className="grid md:grid-cols-2 gap-6">
              {caseStudies.map((study) => (
                <div key={study.id} className="bg-white rounded-lg shadow p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-slate-900">{study.client}</h3>
                      <p className="text-sm text-blue-600">{study.service}</p>
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingCaseStudy(study);
                          setShowCaseStudyForm(true);
                        }}
                      >
                        <Edit size={14} />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => deleteCaseStudy(study.id)}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 mb-2">{study.challenge.substring(0, 100)}...</p>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">{study.industry}</span>
                    <span className={`px-2 py-1 rounded text-xs ${
                      study.is_published ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'
                    }`}>
                      {study.is_published ? 'Published' : 'Draft'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;